import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Heart, Globe, ChevronDown, Menu, X, LogOut, User, BookOpen, Settings } from "lucide-react";
import { AuthContext } from "../clients/utils/AuthContext";

const G      = "#3A9D23";
const G_DARK = "#2f7d1c";
const G_LIGHT = "#f0f9ed";

const NAV_LINKS = [
  { label: "Hébergements", to: "/hotels"       },
  { label: "Destinations",  to: "/destinations" },
  { label: "À propos",      to: "/about"        },
  { label: "Contact",       to: "/contact"      },
];

function KomorotelLogo({ size = 36, light = false }) {
  const c = light ? "#fff" : G;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
      <circle cx="40" cy="40" r="38" fill={light ? "rgba(255,255,255,0.15)" : `${G}18`} />
      <path d="M22 55 Q15 40 22 27 Q30 15 42 14" stroke={c} strokeWidth="3.5" strokeLinecap="round" fill="none"/>
      <path d="M22 55 Q28 63 38 64"              stroke={c} strokeWidth="3.5" strokeLinecap="round" fill="none"/>
      <path d="M38 64 Q50 68 54 57"              stroke={c} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      <path d="M38 64 Q35 58 40 54 Q45 50 42 44" stroke={c} strokeWidth="2"   strokeLinecap="round" fill="none"/>
      <rect x="39" y="12" width="7" height="48" rx="1.5" fill={c}/>
      <path d="M46 40 L64 24 L64 27 L52 40 L64 56 L61 56 L46 42 Z" fill={c}/>
      <circle cx="28" cy="28" r="2.5" fill={c}/>
      <circle cx="25" cy="36" r="2.5" fill={c}/>
      <circle cx="24" cy="44" r="2.5" fill={c}/>
      <circle cx="25" cy="52" r="2.5" fill={c}/>
    </svg>
  );
}

/** Génère les initiales depuis le nom ou l'email */
function getInitials(user) {
  if (user?.firstName && user?.lastName)
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  if (user?.username)
    return user.username.slice(0, 2).toUpperCase();
  if (user?.email)
    return user.email.slice(0, 2).toUpperCase();
  return "?";
}

/** Avatar circulaire avec initiales ou photo */
function Avatar({ user, size = 34, transparent }) {
  const initials = getInitials(user);
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: transparent ? "rgba(255,255,255,0.22)" : G_LIGHT,
      border: `2px solid ${transparent ? "rgba(255,255,255,0.5)" : G}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 800,
      color: transparent ? "#fff" : G,
      cursor: "pointer",
      overflow: "hidden",
      flexShrink: 0,
      transition: "all 0.2s",
      letterSpacing: "0.03em",
    }}>
      {user?.avatar
        ? <img src={user.avatar} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : initials
      }
    </div>
  );
}

/** Menu déroulant utilisateur connecté */
function UserDropdown({ user, onClose, onLogout }) {
  const navigate = useNavigate();

  const go = (path) => { navigate(path); onClose(); };

  const items = [
    { icon: <User size={15}/>,     label: "Mon profil",       action: () => go("/profile")      },
    { icon: <BookOpen size={15}/>, label: "Mes réservations", action: () => go("/my-reservations") },
    { icon: <Heart size={15}/>,    label: "Mes favoris",      action: () => go("/favorites")    },
  ];

  return (
    <>
      {/* Backdrop invisible pour fermer */}
      <div
        style={{ position: "fixed", inset: 0, zIndex: 150 }}
        onClick={onClose}
      />
      <div style={{
        position: "absolute", top: "calc(100% + 10px)", right: 0,
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
        border: "1px solid #f0f0f0",
        minWidth: 220,
        zIndex: 200,
        overflow: "hidden",
        animation: "dropIn 0.18s cubic-bezier(0.34,1.56,0.64,1)",
      }}>
        <style>{`
          @keyframes dropIn {
            from { opacity: 0; transform: translateY(-8px) scale(0.96); }
            to   { opacity: 1; transform: translateY(0)    scale(1);    }
          }
        `}</style>

        {/* En-tête utilisateur */}
        <div style={{
          padding: "14px 16px 12px",
          borderBottom: "1px solid #f3f4f6",
          background: G_LIGHT,
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#1f2937" }}>
            {user?.firstName
              ? `${user.firstName} ${user.lastName ?? ""}`.trim()
              : user?.username ?? "Mon compte"}
          </div>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
            {user?.email}
          </div>
        </div>

        {/* Items */}
        <div style={{ padding: "6px 0" }}>
          {items.map((item, i) => (
            <button key={i} onClick={item.action} style={{
              display: "flex", alignItems: "center", gap: 10,
              width: "100%", padding: "9px 16px",
              background: "none", border: "none", cursor: "pointer",
              fontSize: 13, color: "#374151", fontWeight: 500,
              textAlign: "left",
              transition: "background 0.15s, color 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = G_LIGHT; e.currentTarget.style.color = G; }}
            onMouseLeave={e => { e.currentTarget.style.background = "none";  e.currentTarget.style.color = "#374151"; }}>
              <span style={{ color: "inherit", display: "flex" }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>

        {/* Déconnexion */}
        <div style={{ borderTop: "1px solid #f3f4f6", padding: "6px 0 6px" }}>
          <button onClick={onLogout} style={{
            display: "flex", alignItems: "center", gap: 10,
            width: "100%", padding: "9px 16px",
            background: "none", border: "none", cursor: "pointer",
            fontSize: 13, color: "#dc2626", fontWeight: 600,
            textAlign: "left",
            transition: "background 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
          onMouseLeave={e => e.currentTarget.style.background = "none"}>
            <LogOut size={15} />
            Se déconnecter
          </button>
        </div>
      </div>
    </>
  );
}

export default function Navbar() {
  const [mobileOpen,    setMobileOpen]    = useState(false);
  const [scrolled,      setScrolled]      = useState(false);
  const [dropdownOpen,  setDropdownOpen]  = useState(false);

  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, dispatch } = useContext(AuthContext);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Fermer le dropdown au changement de page
  useEffect(() => { setDropdownOpen(false); setMobileOpen(false); }, [location.pathname]);

  const isHome      = location.pathname === "/";
  const transparent = isHome && !scrolled;

  const bg        = transparent ? "transparent" : "#fff";
  const shadow    = transparent ? "none"        : "0 1px 6px rgba(0,0,0,0.06)";
  const border    = transparent ? "transparent" : "#f3f4f6";
  const logoC     = transparent ? "#fff"                   : G;
  const logoSub   = transparent ? "rgba(255,255,255,0.7)"  : "#9ca3af";
  const linkC     = transparent ? "rgba(255,255,255,0.92)" : "#4b5563";
  const linkHover = transparent ? "#fff"                   : G;
  const iconC     = transparent ? "rgba(255,255,255,0.85)" : "#6b7280";
  const langBd    = transparent ? "rgba(255,255,255,0.35)" : "#e5e7eb";

  const handleLogout = () => {
    dispatch({ type: "LOGOUT" });
    setDropdownOpen(false);
    setMobileOpen(false);
    navigate("/");
  };

  return (
    <>
      <style>{`
        .nb-nav    { display: flex; }
        .nb-right  { display: flex; }
        .nb-burger { display: none; }
        @media (max-width: 900px) {
          .nb-nav    { display: none !important; }
          .nb-right  { display: none !important; }
          .nb-burger { display: flex !important; }
        }
        .nb-avatar-btn:hover > div {
          border-color: ${G_DARK} !important;
          box-shadow: 0 0 0 3px ${G}22;
        }
      `}</style>

      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: bg,
        borderBottom: `1px solid ${border}`,
        boxShadow: shadow,
        transition: "background 0.3s, box-shadow 0.3s, border-color 0.3s",
      }}>
        <div style={{
          maxWidth: 1280, margin: "0 auto", padding: "0 24px",
          height: 64, display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 16,
        }}>

          {/* Logo */}
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none", flexShrink: 0 }}>
            <KomorotelLogo size={36} light={transparent} />
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: "0.05em", color: logoC, lineHeight: 1.1, transition: "color 0.3s" }}>
                KOMOROTEL
              </div>
              <div style={{ fontSize: 10, color: logoSub, letterSpacing: "0.03em", marginTop: 1, transition: "color 0.3s" }}>
                L'archipel, votre destination.
              </div>
            </div>
          </Link>

          {/* Nav desktop */}
          <nav className="nb-nav" style={{ alignItems: "center", gap: 6, flex: 1, justifyContent: "center" }}>
            {NAV_LINKS.map(l => (
              <Link key={l.to} to={l.to}
                style={{ textDecoration: "none", fontSize: 13, fontWeight: 500, color: linkC, transition: "color 0.2s", padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap" }}
                onMouseEnter={e => e.currentTarget.style.color = linkHover}
                onMouseLeave={e => e.currentTarget.style.color = linkC}>
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Right */}
          <div className="nb-right" style={{ alignItems: "center", gap: 8, flexShrink: 0 }}>

            <button style={{ display: "flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", color: iconC, padding: "6px 8px", borderRadius: 8, fontSize: 13, transition: "color 0.3s", whiteSpace: "nowrap" }}>
              <Heart size={15} /><span style={{ marginLeft: 4 }}>Favoris</span>
            </button>

            <button style={{ display: "flex", alignItems: "center", background: "none", border: `1px solid ${langBd}`, borderRadius: 7, cursor: "pointer", color: iconC, padding: "5px 8px", gap: 3, transition: "all 0.3s" }}>
              <Globe size={14} /><span style={{ margin: "0 2px", fontSize: 13 }}>FR</span><ChevronDown size={12} />
            </button>

            {/* ── Connecté : avatar + dropdown ── */}
            {user ? (
              <div style={{ position: "relative" }}>
                <button
                  className="nb-avatar-btn"
                  onClick={() => setDropdownOpen(o => !o)}
                  style={{
                    display: "flex", alignItems: "center", gap: 7,
                    background: "none", border: "none", cursor: "pointer",
                    padding: "3px 6px 3px 3px",
                    borderRadius: 99,
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = transparent ? "rgba(255,255,255,0.1)" : "#f9fafb"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >
                  <Avatar user={user} size={34} transparent={transparent} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: transparent ? "rgba(255,255,255,0.92)" : "#374151", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {user?.firstName ?? user?.username ?? "Mon compte"}
                  </span>
                  <ChevronDown size={13} color={transparent ? "rgba(255,255,255,0.7)" : "#9ca3af"}
                    style={{ transition: "transform 0.2s", transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                </button>

                {dropdownOpen && (
                  <UserDropdown user={user} onClose={() => setDropdownOpen(false)} onLogout={handleLogout} />
                )}
              </div>
            ) : (
              /* ── Non connecté : bouton Se connecter ── */
              <button onClick={() => navigate("/login")}
                style={{ background: G, color: "#fff", border: "none", borderRadius: 9, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "background 0.18s", whiteSpace: "nowrap" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
                Se connecter
              </button>
            )}
          </div>

          {/* Burger */}
          <button className="nb-burger"
            onClick={() => setMobileOpen(true)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 6, alignItems: "center", justifyContent: "center" }}>
            <Menu size={22} color={transparent ? "#fff" : "#374151"} />
          </button>
        </div>

        {/* ── Mobile overlay ── */}
        {mobileOpen && (
          <div style={{ position: "fixed", inset: 0, background: "#fff", zIndex: 999, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: "1px solid #f3f4f6" }}>
              <Link to="/" style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none" }} onClick={() => setMobileOpen(false)}>
                <KomorotelLogo size={32} />
                <div style={{ fontWeight: 800, fontSize: 14, color: G }}>KOMOROTEL</div>
              </Link>
              <button style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => setMobileOpen(false)}>
                <X size={22} color="#374151" />
              </button>
            </div>

            <div style={{ flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 4, overflowY: "auto" }}>

              {/* Si connecté : bloc profil mobile */}
              {user && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 16px", marginBottom: 8,
                  background: G_LIGHT, borderRadius: 12,
                  border: `1px solid ${G}22`,
                }}>
                  <Avatar user={user} size={40} transparent={false} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#1f2937" }}>
                      {user?.firstName ? `${user.firstName} ${user.lastName ?? ""}`.trim() : user?.username}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>{user?.email}</div>
                  </div>
                </div>
              )}

              {NAV_LINKS.map(l => (
                <Link key={l.to} to={l.to}
                  style={{ textDecoration: "none", fontSize: 15, fontWeight: 500, color: "#1f2937", padding: "12px 16px", borderRadius: 10, display: "block" }}
                  onClick={() => setMobileOpen(false)}>
                  {l.label}
                </Link>
              ))}

              {/* Si connecté : liens compte mobile */}
              {user && (
                <>
                  <div style={{ borderTop: "1px solid #f3f4f6", marginTop: 8, paddingTop: 8 }}>
                    {[
                      { icon: <User size={15}/>,     label: "Mon profil",       to: "/profile"       },
                      { icon: <BookOpen size={15}/>, label: "Mes réservations", to: "/my-reservations"  },
                      { icon: <Heart size={15}/>,    label: "Mes favoris",      to: "/favorites"     },
                    ].map((item, i) => (
                      <Link key={i} to={item.to}
                        style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10, fontSize: 14, fontWeight: 500, color: "#374151", padding: "11px 16px", borderRadius: 10 }}
                        onClick={() => setMobileOpen(false)}>
                        <span style={{ color: G }}>{item.icon}</span>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                  <div style={{ borderTop: "1px solid #f3f4f6", marginTop: 4, paddingTop: 8 }}>
                    <button onClick={handleLogout} style={{
                      display: "flex", alignItems: "center", gap: 10,
                      width: "100%", padding: "11px 16px",
                      background: "none", border: "none", cursor: "pointer",
                      fontSize: 14, fontWeight: 600, color: "#dc2626", borderRadius: 10,
                    }}>
                      <LogOut size={15} /> Se déconnecter
                    </button>
                  </div>
                </>
              )}

              {/* Si non connecté : bouton Se connecter */}
              {!user && (
                <div style={{ borderTop: "1px solid #f3f4f6", marginTop: 12, paddingTop: 12 }}>
                  <button
                    style={{ background: G, color: "#fff", border: "none", borderRadius: 9, padding: "12px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", width: "100%" }}
                    onClick={() => { navigate("/login"); setMobileOpen(false); }}>
                    Se connecter
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
}